import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { ITicket } from "../models/ITicket";
import { getTicketById } from "../services/ticketService";

interface Props {
  ticketId: string;
  onClose: () => void;
}

const PRIORITY: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: "Düşük",  color: "#16a34a", bg: "#f0fdf4" },
  2: { label: "Orta",   color: "#2563eb", bg: "#eff6ff" },
  3: { label: "Yüksek", color: "#ea580c", bg: "#fff7ed" },
  4: { label: "Kritik", color: "#dc2626", bg: "#fef2f2" },
};

const STATUS: Record<string, { color: string; bg: string; border: string }> = {
  "Açık":             { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  "Ticket yaratıldı": { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  "Kapalı":           { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  "Kapatıldı":        { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  "Beklemede":        { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
};

function getStatus(desc?: string) {
  return STATUS[desc ?? ""] ?? { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function ChildTicketPreviewModal({ ticketId, onClose }: Props) {
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<ITicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setTicket(null);
    setError("");
    setLoading(true);
    getTicketById(ticketId)
      .then((res) => setTicket(res.data))
      .catch(() => setError("Ticket yüklenemedi."))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const priority = ticket ? (PRIORITY[ticket.priority] ?? PRIORITY[1]) : null;
  const status   = ticket ? getStatus(ticket.ticketStatusDescription) : null;
  const previewMessages = (ticket?.messages ?? []).filter(m => !m.message.startsWith("@TicketBilgi "));

  const goToTicket = () => {
    onClose();
    navigate(`/ticket/${ticketId}`);
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20, width: "100%", maxWidth: 520, maxHeight: "85vh",
          boxShadow: "0 12px 48px rgba(0,0,0,0.22)", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 18px",
          background: "linear-gradient(135deg, #059669, #047857)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bi bi-eye-fill text-white" style={{ fontSize: "1.1rem" }}></i>
            </div>
            <div style={{ minWidth: 0 }}>
              {ticket?.ticketNo && (
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.8)", marginBottom: 2 }}>
                  <i className="bi bi-hash me-1"></i>{ticket.ticketNo}
                </div>
              )}
              <div style={{
                fontWeight: 800, fontSize: "0.95rem", color: "#fff",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {ticket?.title ?? "Yükleniyor..."}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: 8, width: 32, height: 32, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#fff", fontSize: "1.1rem",
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1, minHeight: 160 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div className="spinner-border text-primary spinner-border-sm" role="status" />
            </div>
          ) : error || !ticket ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>
              <i className="bi bi-exclamation-circle-fill" style={{ fontSize: "1.6rem", display: "block", marginBottom: 8 }}></i>
              <span style={{ fontSize: "0.85rem" }}>{error || "Ticket bulunamadı."}</span>
            </div>
          ) : (
            <>
              {/* Badges */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {status && (
                  <span style={{
                    fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px",
                    borderRadius: 20, background: status.bg, color: status.color,
                    border: `1px solid ${status.border}`,
                  }}>
                    {ticket.ticketStatusDescription}
                  </span>
                )}
                {priority && (
                  <span style={{
                    fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px",
                    borderRadius: 20, background: priority.bg, color: priority.color,
                  }}>
                    <i className="bi bi-flag-fill me-1"></i>{priority.label}
                  </span>
                )}
              </div>

              {/* Opening note */}
              {ticket.note && (
                <div style={{
                  marginBottom: 14, padding: "12px 14px", borderRadius: 12,
                  background: "linear-gradient(135deg, #fffbeb, #fef9c3)",
                  border: "1.5px solid #fde68a",
                }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                    <i className="bi bi-chat-square-text-fill me-1"></i>Açılış Mesajı — {ticket.mainCustomerName}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#78350f", lineHeight: 1.55 }}>
                    {ticket.note}
                  </div>
                </div>
              )}

              {/* Messages preview */}
              {previewMessages.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>
                  <i className="bi bi-chat-square" style={{ fontSize: "1.5rem", display: "block", marginBottom: 6, opacity: 0.4 }}></i>
                  <span style={{ fontSize: "0.8rem" }}>Henüz mesaj yok</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {previewMessages.map((msg) => (
                    <div key={msg.id} style={{
                      padding: "10px 12px", borderRadius: 10,
                      background: "#f3f4f6", border: "1px solid #e5e7eb",
                    }}>
                      <div style={{ fontSize: "0.72rem", color: "#6b7280", marginBottom: 4 }}>
                        <strong style={{ color: "#374151" }}>{msg.customerName}</strong>
                        {" · "}{formatDate(msg.createdAt)}
                      </div>
                      <div style={{ fontSize: "0.84rem", color: "#374151", lineHeight: 1.5, wordBreak: "break-word" }} className="markdown-bubble">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{msg.message}</ReactMarkdown>
                      </div>
                      {msg.imageUrl && (
                        <img
                          src={msg.imageUrl}
                          alt="ek"
                          style={{ display: "block", marginTop: 8, maxWidth: "100%", maxHeight: 180, borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)" }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa", flexShrink: 0, display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: 10, borderRadius: 10,
              border: "1.5px solid #e5e7eb", background: "#fff",
              color: "#374151", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer",
            }}
          >
            Kapat
          </button>
          <button
            onClick={goToTicket}
            disabled={!ticket}
            style={{
              flex: 2, padding: 10, borderRadius: 10, border: "none",
              background: ticket ? "linear-gradient(135deg, #059669, #047857)" : "#e5e7eb",
              color: ticket ? "#fff" : "#9ca3af", fontWeight: 700, fontSize: "0.85rem",
              cursor: ticket ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <i className="bi bi-box-arrow-up-right"></i> Tickete Git
          </button>
        </div>
      </div>
    </div>
  );
}
